import { useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import type {
  ColDef,
  SortChangedEvent,
  RowClickedEvent,
} from "ag-grid-community";
import "./style.css";

ModuleRegistry.registerModules([AllCommunityModule]);

export type UIGridSortOrder = "ascend" | "descend" | undefined;

export type UIAgGridColumn<T extends Record<string, unknown>> = {
  key: string;
  title: string;
  dataIndex: keyof T | string;
  width?: number;
  minWidth?: number;
  flex?: number;
  sorter?: boolean;
  defaultHidden?: boolean;
  headerClass?: string;
  cellClass?: string;
  render?: (value: unknown, row: T) => React.ReactNode;
};

type UIAgGridTableProps<T extends Record<string, unknown>> = {
  columns: UIAgGridColumn<T>[];
  data: T[];
  loading?: boolean;
  className?: string;
  height?: number | string;
  rowHeight?: number;
  onSortChange?: (sort: { field?: string; order?: UIGridSortOrder }) => void;
  onRowClick?: (row: T) => void;
};

const UIAgGridTable = <T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  className,
  height,
  rowHeight = 48,
  onSortChange,
  onRowClick,
}: UIAgGridTableProps<T>) => {
  const gridRef = useRef<AgGridReact<T>>(null);

  // High-fidelity Enterprise Empty State Template (Fixed HTML string quoting)
  const noRowsTemplate = useMemo(() => {
    return `
      <div class="ui-ag-grid-empty-state">
        <div class="ui-ag-grid-empty-icon-wrapper">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 17h6" />
            <path d="M9 13h6" />
            <path d="M9 9h1" />
          </svg>
        </div>
        <div class="ui-ag-grid-empty-title">No records found</div>
        <div class="ui-ag-grid-empty-subtitle">There is no data to display in this view at the moment.</div>
      </div>
    `;
  }, []);

  const colDefs = useMemo<ColDef<T>[]>(
    () =>
      columns.map((column) => ({
        colId: column.key,
        headerName: column.title,
        sortable: Boolean(column.sorter),
        resizable: true,
        width: column.width,
        minWidth: column.minWidth,
        flex: column.flex,
        suppressMovable: true,
        headerClass: column.headerClass,
        cellClass: column.cellClass,
        valueGetter: (params) => {
          if (!params.data) {
            return undefined;
          }
          return (params.data as Record<string, unknown>)[
            String(column.dataIndex)
          ];
        },
        cellRenderer: (params: { value: unknown; data: T }) => {
  const row = params.data as Record<string, any>;

  if (row?.__skeleton) {
    return (
      <div className="h-4 w-[80%] rounded-md bg-slate-200 animate-pulse" />
    );
  }

  if (column.render) {
    return column.render(params.value, params.data);
  }

  return params.value ?? "";
},
      })),
    [columns],
  );

  const handleSortChanged = (event: SortChangedEvent<T>) => {
    if (!onSortChange) {
      return;
    }

    const sorted = event.api
      .getColumnState()
      .filter((state) => Boolean(state.sort))
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

    const firstSort = sorted[0];
    if (!firstSort?.colId || !firstSort.sort) {
      onSortChange({ field: undefined, order: undefined });
      return;
    }

    onSortChange({
      field: firstSort.colId,
      order: firstSort.sort === "asc" ? "ascend" : "descend",
    });
  };

const handleRowClicked = (event: RowClickedEvent<T>) => {
  const row = event.data as Record<string, any>;
  if (row?.__skeleton) return;

  if (onRowClick && event.data) {
    onRowClick(event.data);
  }
};

const getRowClass = useMemo(
  () => () => (onRowClick ? "ui-ag-grid-clickable-row" : ""),
  [onRowClick]
);

  return (
    <div
      className={`ag-theme-alpine ui-ag-grid-wrapper${className ? ` ${className}` : ""}`}
      style={{
        height: height ?? "100%",
        overflow: "hidden",
      }}
    >
      <AgGridReact<T>
        ref={gridRef}
        rowData={data}
        loading={loading}
        columnDefs={colDefs}
        animateRows
        rowHeight={rowHeight}
        headerHeight={40}
        onRowClicked={handleRowClicked}
        getRowClass={getRowClass}
        suppressCellFocus={true}
        defaultColDef={{
          resizable: true,
          suppressSizeToFit: false,
        }}
        enableCellTextSelection
        onSortChanged={handleSortChanged}
        overlayLoadingTemplate='<div class="ui-ag-grid-loading"><span class="ui-ag-grid-spinner" aria-hidden="true"></span><span class="ui-ag-grid-loading-text">Loading data...</span></div>'
        overlayNoRowsTemplate={noRowsTemplate}
      />
    </div>
  );
};

export { UIAgGridTable };
